import React, { useState } from "react";
import { Menu, Search, ChevronDown, LogIn, ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import LogoutButton from "./LogoutButton.js";
import AiModeToggle from "./AiModeToggle.js";
import AiConsoleIconRow from "../chat/AiConsoleIconRow.js";
import { useAiConsoleSlot } from "../chat/AiConsoleSlotContext.js";
import { useDashboardSession, clearDashboardSession } from "../auth/useDashboardSession.js";
import { clearImpersonation, friendlyPlatformError } from "../lib/platformApi.js";

type Props = {
  onOpenSidebar: () => void;
};

export default function Header({ onOpenSidebar }: Props): React.ReactElement {
  const session = useDashboardSession();
  const { slot: aiConsoleSlot } = useAiConsoleSlot();
  const [userOpen, setUserOpen] = useState(false);
  const [clearingImpersonation, setClearingImpersonation] = useState(false);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);
  // Avatar / dropdown content reflect the real session — derived from
  // /v1/auth/me via DashboardSessionProvider. When the session is the
  // "guest" sentinel (not signed in or signed in but no membership)
  // we show a 登入 CTA instead of an avatar dropdown so the header is
  // never visually claiming "you are X" while RequireRole has bounced
  // the user to /forbidden.
  const isAuthed = session.role !== "guest";
  const displayName = session.display_name?.trim() || session.email || "使用者";
  const initials = computeInitials(displayName);
  const sessionEmail = session.email ?? "";
  const isImpersonating = !!session.acting_tenant_id;
  const actingName = session.acting_tenant_name ?? session.acting_tenant_id ?? "";

  const onEndImpersonation = async () => {
    setClearingImpersonation(true);
    setImpersonationError(null);
    try {
      await clearImpersonation();
      clearDashboardSession();
      // Hard reload so /v1/auth/me re-fetches the new session
      // (DashboardSessionProvider only bootstraps once per page load).
      window.location.assign("/dashboard/platform/tenants");
    } catch (e) {
      setImpersonationError(friendlyPlatformError(e));
    } finally {
      setClearingImpersonation(false);
    }
  };

  return (
    <header data-tour-header-anchor className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-bg-base/85 backdrop-blur-md px-3 sm:px-4">
      <button
        type="button"
        onClick={onOpenSidebar}
        data-tour-id="ui.sidebar.open"
        className="btn-ghost h-9 w-9 p-0 lg:hidden"
        aria-label="開啟選單"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Impersonation chip — visible whenever the session carries an
          acting_tenant_id. Only super_admin accounts can ever have
          acting_tenant_id set, so presence is a sufficient signal
          (session.super_admin is EFFECTIVE per strict-isolation model
          — false during impersonation — so gating on it would hide
          the chip exactly when we need it). */}
      {isImpersonating && (
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard/platform/tenants"
            className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-sm text-warning hover:bg-warning/15 transition-colors"
            title={`目前以 ${actingName} 身份操作。點此回到客戶單位列表切換或結束代理。`}
          >
            <ShieldCheck className="h-4 w-4" />
            <span className="hidden sm:inline truncate max-w-[180px]">
              代理 <strong className="font-semibold">{actingName}</strong>
            </span>
            <span className="sm:hidden truncate max-w-[80px] font-semibold">{actingName}</span>
          </Link>
          <button
            type="button"
            onClick={() => void onEndImpersonation()}
            disabled={clearingImpersonation}
            className="btn-ghost h-8 px-2 text-xs text-text-secondary hover:text-text-primary"
            title="結束代理，回到平台管理視角"
          >
            {clearingImpersonation ? "結束中…" : (
              <span className="inline-flex items-center gap-1">
                <X className="h-3.5 w-3.5" />
                <span className="hidden md:inline">結束代理</span>
              </span>
            )}
          </button>
        </div>
      )}
      {impersonationError && (
        <div className="text-2xs text-danger truncate max-w-[200px]" title={impersonationError}>
          {impersonationError}
        </div>
      )}

      {/* Search */}
      <div className="ml-2 flex-1 max-w-md hidden md:block">
        <label className="relative block">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
          <input
            type="search"
            placeholder="搜尋地點、案場、報表…"
            className="input pl-9 h-9 text-sm"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:block text-2xs text-text-muted font-mono border border-border rounded px-1.5 py-0.5">
            ⌘K
          </span>
        </label>
      </div>

      {/* Right-aligned cluster — `ml-auto` pins the whole group to the
          right edge regardless of whether the search input or the
          mobile spacer is doing the flex-1 push, so this is the
          source of truth for "Avatar / 登入 should sit on the right"
          across every breakpoint and impersonation state. */}
      <div className="ml-auto flex items-center gap-2">
        {/* AI 導覽 drawer view controls — pushed up by the Topology
            page via AiConsoleSlotContext when in AI mode. Desktop
            only; mobile renders the icon row inside PageHeader
            actions instead because the Header bar is too tight. */}
        {isAuthed && aiConsoleSlot && (
          <div className="hidden md:inline-flex">
            <AiConsoleIconRow
              view={aiConsoleSlot.view}
              onChange={aiConsoleSlot.setView}
              planSupported={aiConsoleSlot.planSupported}
              planPending={aiConsoleSlot.planPending}
            />
          </div>
        )}
        {/* AI / Professional mode toggle — spec
            docs/specs/ai-mode-operations-console.md §1. Hidden for
            guests (not signed in) so the marketing-style header stays
            uncluttered on /login bounces. */}
        {isAuthed && <AiModeToggle />}

        {/* Mobile search button */}
        <button
          type="button"
          className="btn-ghost h-9 w-9 p-0 md:hidden"
          aria-label="搜尋"
        >
          <Search className="h-5 w-5" />
        </button>

        {/* User avatar — real session, never hardcoded. */}
        {!isAuthed ? (
          <Link
            to="/login?return_to=/dashboard/"
            className="btn-primary px-3 py-1.5 text-xs"
            aria-label="登入"
          >
            <LogIn size={14} />
            登入
          </Link>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={() => setUserOpen((v) => !v)}
              className="flex items-center gap-2 rounded-md hover:bg-bg-hover px-1.5 py-1 transition-colors"
              aria-haspopup="menu"
              aria-expanded={userOpen}
              title={displayName}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-text-inverse text-xs font-semibold uppercase">
                {initials}
              </span>
              <ChevronDown className="h-3 w-3 text-text-secondary hidden sm:block" />
            </button>
            {userOpen && (
              <div
                className="absolute right-0 mt-1 w-56 rounded-md border border-border bg-bg-surface shadow-2xl py-1 animate-fade-in z-40"
                role="menu"
              >
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-sm font-medium text-text-primary truncate" title={displayName}>
                    {displayName}
                  </p>
                  {sessionEmail ? (
                    <p
                      className="text-2xs text-text-muted truncate font-mono"
                      title={sessionEmail}
                    >
                      {sessionEmail}
                    </p>
                  ) : null}
                  <p className="text-2xs text-accent mt-1">
                    {session.super_admin ? "super_admin" : session.role}
                  </p>
                </div>
                <Link
                  to="/dashboard/settings"
                  role="menuitem"
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-bg-hover"
                  onClick={() => setUserOpen(false)}
                >
                  個人設定
                </Link>
                <div className="my-1 h-px bg-border" />
                <LogoutButton variant="menuitem" onBeforeLogout={() => setUserOpen(false)} />
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

// Compute 1-2 char uppercase initials from a display name.
//   "Dalton Subramanian" → "DS"
//   "Alice"             → "A"
//   "alice@x.com"       → "A"
//   ""                  → "?"
function computeInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  // If it looks like an email, take the first letter of the local part.
  if (trimmed.includes("@")) {
    return (trimmed[0] ?? "?").toUpperCase();
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0]![0] ?? "?").toUpperCase();
  return ((parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")).toUpperCase();
}
